/**
 * What the reader has narrowed the library to — one value, serialized
 * one way.
 *
 * The browser URL and the API query string are THE SAME STRING.  Two
 * serializers would be two chances to disagree, and the disagreement
 * would surface as the worst kind of bug this page can have: a link a
 * reviewer sent a colleague that opens on a different shelf.  So the
 * page's address bar carries exactly the query it sends, and a pasted
 * URL reproduces the view.
 *
 * Everything here is pure — no React, no fetch — so the whole rule is
 * checkable without a browser (`apps/coaching/tests/libraryFilters.test.ts`).
 */

/** Where in the tree the reader is standing. */
export interface Scope {
  ring: string;
  planet: string;
  claim: string;
  /** The bucket for everything no ring reaches. */
  outside: boolean;
}

/** The attribute chips, each a set; empty means "no opinion". */
export interface Chips {
  /**
   * `level` and `type` are ANY-OF questions about a unit's INSTRUMENTS: a unit
   * matches when any instrument it embeds does.  So one unit answers to
   * several values of the same chip, and the counts beside the chips add
   * up to more than the rows on screen.  That is the honest arithmetic
   * of this grain rather than a bug to reconcile.
   */
  level: string[];
  type: string[];
  /**
   * The unit's OWN shape — lesson_bench, scenario_chairs, wager_deck,
   * drills, testout, onboarding — and the filter this whole vocabulary
   * rename existed to make possible.  Single-valued per unit, so its
   * tallies do sum to the row count.
   */
  vessel: string[];
  assessment: string[];
  mode: string[];
  demonstrates: string[];
  provenance: string[];
  /** Hide drained (tombstoned) units.  Off by default: a drained unit is
   *  exactly the kind of thing a reviewer came to find. */
  hideDrained: boolean;
  /** Only units naming a claim no roster holds. */
  danglingOnly: boolean;
  /**
   * Show only these verdicts — the flagged-only sweep.
   */
  review: string[];
  /**
   * Get what has been signed off out of the way.  NOT the same as
   * `review: ["pending"]`: a flagged unit has been read but is not done
   * with, so draining the queue has to keep it visible.
   */
  excludeReviewed: boolean;
}

export interface LibraryQuery {
  scope: Scope;
  chips: Chips;
  search: string;
  /**
   * Which authoring pipeline's shelves the reader is looking at —
   * "" for all of them.
   *
   * Neither a scope nor a chip.  Not a scope, because it survives
   * walking the tree: "show me what v2 made" is a question a reviewer
   * carries from ring to planet, and `scopeTo` clears the scope at
   * every click.  Not a chip, because the server counts the facets
   * WITHIN it — so every count on the page is "how many of what this
   * pipeline made", which is exactly what makes a v1-versus-v2
   * comparison legible at a glance rather than arithmetic.
   */
  pipeline: string;
}

/** The chip groups, in the order the filter bar draws them. */
export const CHIP_GROUPS = [
  "level",
  "type",
  "vessel",
  "review",
  "assessment",
  "mode",
  "demonstrates",
  "provenance",
] as const;

export type ChipGroup = (typeof CHIP_GROUPS)[number];

/**
 * The facet bucket meaning "nothing declared", per group.
 *
 * The server counts these so a reviewer can see that eleven units embed
 * no graded instrument at all, but the wire's filters are CLOSED vocabularies
 * and drop anything outside them — so drawing a chip for one of these
 * buckets would put a dead control on the page.  The count belongs to
 * the facet; the chip does not exist.
 */
export const EMPTY_BUCKET: Record<ChipGroup, string> = {
  level: "0",
  type: "—",
  vessel: "—",
  review: "—",
  assessment: "—",
  mode: "—",
  demonstrates: "—",
  provenance: "—",
};

export const EMPTY_QUERY: LibraryQuery = {
  scope: { ring: "", planet: "", claim: "", outside: false },
  chips: {
    level: [],
    type: [],
    vessel: [],
    assessment: [],
    mode: [],
    demonstrates: [],
    provenance: [],
    hideDrained: false,
    danglingOnly: false,
    review: [],
    excludeReviewed: false,
  },
  search: "",
  pipeline: "",
};

/** Add or remove one value from a chip group, order-stable. */
export function toggle(values: string[], value: string): string[] {
  return values.includes(value) ? values.filter((v) => v !== value) : [...values, value];
}

/** True when any attribute chip is on — drives the "clear" affordance. */
export function hasChips(chips: Chips): boolean {
  return (
    CHIP_GROUPS.some((group) => chips[group].length > 0) ||
    chips.hideDrained ||
    chips.danglingOnly ||
    chips.excludeReviewed
  );
}

/**
 * Narrow to one place in the tree, clearing what the new place makes
 * meaningless.
 *
 * Selecting a ring must forget the planet, because a planet from
 * another ring intersected with this one is an empty shelf and reads as
 * "there is nothing here" rather than "you asked for two places at
 * once".  The attribute chips SURVIVE the move on purpose: "show me
 * every bench holding a level 3 instrument" is a question a reviewer carries
 * from planet to planet, and dropping it at every click would make the
 * chips useless.
 */
export function scopeTo(query: LibraryQuery, scope: Partial<Scope>): LibraryQuery {
  const next: Scope = { ...EMPTY_QUERY.scope, ...scope };
  return { ...query, scope: next };
}

/** A short human label for where the reader is standing. */
export function scopeCrumbs(scope: Scope): string[] {
  if (scope.outside) return ["Outside the rings", scope.planet, scope.claim].filter(Boolean);
  return [scope.ring, scope.planet, scope.claim].filter(Boolean);
}

/**
 * Serialize to the query string — the API's and the address bar's.
 *
 * Only non-default values are written, so a fresh view has a clean URL
 * and two equal queries always produce the same string (the round-trip
 * test leans on that).
 */
export function toQueryString(query: LibraryQuery): string {
  const params = new URLSearchParams();
  const { scope, chips } = query;
  if (scope.outside) params.set("outside", "1");
  if (scope.ring) params.set("ring", scope.ring);
  if (scope.planet) params.set("planet", scope.planet);
  if (scope.claim) params.set("claim", scope.claim);
  // Every chip group's name IS its wire parameter, which is why the loop
  // can be blind: `vessel=lesson_bench,drills` needed no new branch here.
  for (const group of CHIP_GROUPS) {
    if (chips[group].length) params.set(group, chips[group].join(","));
  }
  // The wire says "include tombstoned", the chip says "hide drained".
  // The wire's default is to include, so the chip only ever writes the
  // negative — which is why this is not a plain mirror of the flag.
  if (chips.hideDrained) params.set("tombstoned", "0");
  if (chips.danglingOnly) params.set("dangling", "1");
  if (chips.excludeReviewed) params.set("excludeReviewed", "1");
  if (query.pipeline) params.set("pipeline", query.pipeline);
  if (query.search) params.set("q", query.search);
  return params.toString();
}

const TRUE_VALUES = new Set(["1", "true", "yes", "on"]);

function list(params: URLSearchParams, key: string): string[] {
  const raw = params.get(key);
  if (!raw) return [];
  return raw
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
}

/** Parse a query string back into the value it came from. */
export function fromQueryString(search: string): LibraryQuery {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  return {
    scope: {
      ring: params.get("ring") ?? "",
      planet: params.get("planet") ?? "",
      claim: params.get("claim") ?? "",
      outside: TRUE_VALUES.has((params.get("outside") ?? "").toLowerCase()),
    },
    chips: {
      level: list(params, "level"),
      type: list(params, "type"),
      vessel: list(params, "vessel"),
      assessment: list(params, "assessment"),
      mode: list(params, "mode"),
      demonstrates: list(params, "demonstrates"),
      provenance: list(params, "provenance"),
      hideDrained: (params.get("tombstoned") ?? "") === "0",
      danglingOnly: TRUE_VALUES.has((params.get("dangling") ?? "").toLowerCase()),
      review: list(params, "review"),
      excludeReviewed: TRUE_VALUES.has((params.get("excludeReviewed") ?? "").toLowerCase()),
    },
    search: params.get("q") ?? "",
    pipeline: params.get("pipeline") ?? "",
  };
}

/**
 * Whether a planet still has anything for a reviewer to read.
 *
 * A planet has no verdict of its own — it is a place, and places are
 * not reviewed, their contents are.  So "needs a read" is a question
 * about its UNITS: any still unread, or any flagged and therefore not
 * done with.  A phantom group always shows: it holds units nobody can
 * review through a planet that does not exist, and hiding a defect
 * behind a review filter is how defects survive.
 */
export function needsReview(planet: { unread: number; flagged: number; exists: boolean }): boolean {
  if (!planet.exists) return true;
  return planet.unread > 0 || planet.flagged > 0;
}
